
import { useState, FormEvent } from 'react';

export default function BookingForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    preferredDay: '',
    serviceType: '',
    projectDetails: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formBody = new URLSearchParams();
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('address', formData.address);
    formBody.append('postcode', formData.postcode);
    formBody.append('preferredDay', formData.preferredDay);
    formBody.append('serviceType', formData.serviceType);
    formBody.append('projectDetails', formData.projectDetails);

    try {
      const response = await fetch('https://readdy.ai/api/form/d610bo8omoleh5thvd2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          postcode: '',
          preferredDay: '',
          serviceType: '',
          projectDetails: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
            Request a Site Visit
          </h2>
          <p className="text-neutral-600">
            Fill in your details below and we&apos;ll contact you to confirm your appointment
          </p>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-lg font-bold text-green-800 mb-2">Booking Request Received!</h3>
            <p className="text-green-700 text-sm">
              Thank you for your enquiry. We&apos;ll contact you within 24 hours to confirm your site
              visit appointment.
            </p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-bold text-red-800 mb-2">Something Went Wrong</h3>
            <p className="text-red-700 text-sm">
              Please try again or call us directly at 07595 037441
            </p>
          </div>
        )}

        <form
          id="site-visit-booking-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-beige-50 rounded-lg p-8 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                placeholder="07XXX XXXXXX"
              />
            </div>
            <div>
              <label htmlFor="postcode" className="block text-sm font-medium text-neutral-700 mb-2">
                Postcode *
              </label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                placeholder="WD17 1AA"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="address" className="block text-sm font-medium text-neutral-700 mb-2">
              Property Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
              placeholder="123 High Street, Watford"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="preferredDay" className="block text-sm font-medium text-neutral-700 mb-2">
                Preferred Day *
              </label>
              <select
                id="preferredDay"
                name="preferredDay"
                value={formData.preferredDay}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 cursor-pointer"
              >
                <option value="">Select a day</option>
                <option value="Monday">Monday (5-7 PM)</option>
                <option value="Tuesday">Tuesday (5-7 PM)</option>
                <option value="Wednesday">Wednesday (5-7 PM)</option>
                <option value="Thursday">Thursday (5-7 PM)</option>
                <option value="Friday">Friday (5-7 PM)</option>
                <option value="Saturday">Saturday (5-7 PM)</option>
              </select>
            </div>
            <div>
              <label htmlFor="serviceType" className="block text-sm font-medium text-neutral-700 mb-2">
                Service Required *
              </label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="Interior Painting">Interior Painting</option>
                <option value="Exterior Painting">Exterior Painting</option>
                <option value="Wallpapering">Wallpapering</option>
                <option value="Decorating">Decorating</option>
                <option value="Full Property">Full Property Renovation</option>
                <option value="Commercial">Commercial Project</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="projectDetails" className="block text-sm font-medium text-neutral-700 mb-2">
              Project Details
            </label>
            <textarea
              id="projectDetails"
              name="projectDetails"
              value={formData.projectDetails}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none"
              placeholder="Tell us about your project - number of rooms, current condition, any specific requirements..."
            ></textarea>
            <p className="text-xs text-neutral-500 mt-1">
              {formData.projectDetails.length}/500 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="ri-calendar-check-line"></i>
                Request Site Visit
              </>
            )}
          </button>

          <p className="text-xs text-neutral-500 text-center mt-4">
            By submitting this form, you agree to be contacted regarding your enquiry. We'll confirm
            your appointment within 24 hours.
          </p>
        </form>

        <div className="mt-12 text-center">
          <p className="text-neutral-600 mb-4">Prefer to speak with us directly?</p>
          <a
            href="tel:+447595037441"
            className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-800 hover:bg-neutral-900 text-white font-medium rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="fas fa-phone"></i>
            Call 07595 037441
          </a>
        </div>
      </div>
    </section>
  );
}
